import React, { useEffect, useState } from 'react'
import { Row, Col, Card, CardBody, CardTitle, Button, Badge, Input, Label, FormGroup, Table, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Spinner } from 'reactstrap'
import { useSetRecoilState } from 'recoil'
import moment from 'moment'
import { pageState } from '../../../state/GlobalState'
import { HasPermission } from '../../../custom/helpers/HasPermission'
import { handleResponse } from '../../../custom/helpers/HandleResponse'
import {
  getStatusRequest,
  getSettingsRequest,
  saveSettingsRequest,
  syncRequest,
  getLogsRequest,
  getPendingDeletionsRequest,
  finalizeRequest,
  unfinalizeRequest,
} from '../../../api/controller/LapStartController'

const statusColor = {
  success: 'success',
  partial: 'warning',
  failed: 'danger',
  finalized: 'dark',
  blocked: 'warning',
}

export default function LapStart() {
  const setPage = useSetRecoilState(pageState)

  const [status, setStatus] = useState(null)
  const [logs, setLogs] = useState([])
  const [pendingDeletions, setPendingDeletions] = useState([])
  const [blockedDeletions, setBlockedDeletions] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)

  const [settings, setSettings] = useState({ BaseUrl: '', RosterToken: '', SyncEnabled: false, IntervalMinutes: 15 })
  const [savingSettings, setSavingSettings] = useState(false)

  const [resultModal, setResultModal] = useState({ visible: false, log: null })
  const [finalizeModal, setFinalizeModal] = useState(false)

  // HasPermission is a hook and appends the page name, so these resolve to
  // sync-lapstart / update-lapstart / finalize-lapstart. Read once at the top:
  // calling them inside conditional JSX would make the hook order vary between
  // renders.
  const canSyncPermission = HasPermission('sync')
  const canUpdate = HasPermission('update')
  const canFinalize = HasPermission('finalize')

  useEffect(() => {
    setPage('lapstart')
    load()
    // eslint-disable-next-line
  }, [])

  const load = async () => {
    setLoading(true)
    const [s, l, p, cfg] = await Promise.all([
      getStatusRequest({}),
      getLogsRequest({}),
      getPendingDeletionsRequest({}),
      getSettingsRequest({}),
    ])
    setStatus(s?.Data || null)
    setLogs(l?.data || [])
    setPendingDeletions(p?.data || [])
    setBlockedDeletions(p?.blocked || [])
    if (cfg?.Data) {
      // RosterToken is never returned -- an empty field means "leave the stored
      // one alone", which is why it is not seeded from the response.
      setSettings({
        BaseUrl: cfg.Data.BaseUrl || '',
        RosterToken: '',
        SyncEnabled: !!cfg.Data.SyncEnabled,
        IntervalMinutes: cfg.Data.IntervalMinutes || 15,
      })
    }
    setLoading(false)
  }

  const saveSettings = async () => {
    setSavingSettings(true)
    const response = await saveSettingsRequest(settings)
    handleResponse(response)
    setSavingSettings(false)
    if (response?.Status === 'success') {
      setSettings(prev => ({ ...prev, RosterToken: '' }))
      load()
    }
  }

  const runSync = async () => {
    setSyncing(true)
    const response = await syncRequest({})
    handleResponse(response)
    setSyncing(false)
    if (response?.Data) {
      setResultModal({ visible: true, log: response.Data })
    }
    load()
  }

  const doFinalize = async () => {
    const response = await finalizeRequest({})
    handleResponse(response)
    setFinalizeModal(false)
    load()
  }

  const doUnfinalize = async () => {
    const response = await unfinalizeRequest({})
    handleResponse(response)
    load()
  }

  const fmt = (v) => v ? moment(v).format('MMM Do, YYYY HH:mm:ss') : '—'

  if (loading && !status) {
    return <div className="page-content"><Spinner color="primary" /></div>
  }

  const finalized = status?.Finalized
  const canSync = canSyncPermission && status?.Configured && !finalized

  return (
    <div className="page-content">
      <Row>
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h4 className="mb-0">LapStart Roster Sync</h4>
              <small className="text-muted">{status?.EventName}</small>
            </div>
            <div className="d-flex gap-2">
              <Button color="light" onClick={load} disabled={loading}>
                <i className="mdi mdi-refresh" /> Refresh
              </Button>
              {canSync &&
                <Button color="primary" onClick={runSync} disabled={syncing}>
                  {syncing ? <><Spinner size="sm" /> Syncing…</> : <><i className="mdi mdi-cloud-upload" /> Sync Now</>}
                </Button>
              }
            </div>
          </div>
        </Col>
      </Row>

      {/*
        Finalized is not an error state and must not read like one. From here
        expo sends nothing at all -- not creates, not updates, not deletions.
      */}
      {finalized &&
        <Alert color="dark">
          <h5 className="alert-heading">Finalized — LapStart owns the roster</h5>
          <p className="mb-2">
            Nothing is sent to LapStart from here. Roster changes go through the LapStart ops console.
          </p>
          <div className="small">
            {status.FinalizedReason === 'event_started' && <>The event started, so LapStart froze the roster. </>}
            {status.FinalizedReason === 'event_published' && <>Results are published, so LapStart locked the roster. </>}
            {status.FinalizedReason === 'manual' && <>Finalized by {status.FinalizedBy || 'an operator'}. </>}
            {fmt(status.FinalizedAt)}
          </div>
          {canFinalize &&
            <Button color="link" className="p-0 mt-2" onClick={doUnfinalize}>Undo finalization</Button>
          }
        </Alert>
      }

      {!status?.Configured &&
        <Alert color="warning">
          LapStart is not configured for this event. Add the base URL and the roster token below — ask the
          LapStart operator for both.
        </Alert>
      }

      {/*
        A closed feed is not a dead token, and the copy has to say so plainly.
        Asking for a new credential is the wasted afternoon this state exists to
        prevent.
      */}
      {status?.FeedClosed && !finalized &&
        <Alert color="warning">
          <h5 className="alert-heading">LapStart has closed the expo feed</h5>
          <p className="mb-0">
            A LapStart operator closed this feed — usually once the start list is final.
            <strong> The roster token is still valid; do not request a new one.</strong> Ask them to reopen it in
            the ops console and syncing resumes on its own, with no changes needed here.
          </p>
        </Alert>
      }

      {/*
        Every one of these races' rows would be rejected, so it is a setup
        blocker worth showing before a run rather than after one.
      */}
      {status?.RacesMissingSlug?.length > 0 &&
        <Alert color="danger">
          <strong>These timed races have participants but no LapStart slug:</strong> {status.RacesMissingSlug.join(', ')}.
          <div className="small mt-1">
            Sync will not run until every one is set. Ask the LapStart operator for each slug and set it on the race.
          </div>
        </Alert>
      }

      {/*
        Not a problem to fix. LapStart is a timing system, so a race that is not
        timed has nothing there to sync to -- stated plainly so nobody goes
        looking for the kids' races on LapStart and assumes something broke.
      */}
      {status?.NonTimedRaces?.length > 0 &&
        <Alert color="light" className="border">
          <strong>Not synced — these races are not timed:</strong>{' '}
          {status.NonTimedRaces.map(r => `${r.name} (${r.participants})`).join(', ')}.
          <div className="small text-muted mt-1">
            LapStart only handles timed races. These participants stay in expo and need no slug.
          </div>
        </Alert>
      }

      <Row>
        <Col md={4}>
          <Card>
            <CardBody>
              <CardTitle className="mb-3">Status</CardTitle>
              <Table borderless size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted ps-0">Last sync</td>
                    <td className="text-end pe-0">{fmt(status?.LastSyncAt)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted ps-0">Result</td>
                    <td className="text-end pe-0">
                      {status?.LastSyncStatus
                        ? <Badge color={statusColor[status.LastSyncStatus] || 'secondary'}>{status.LastSyncStatus}</Badge>
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted ps-0">Next due</td>
                    <td className="text-end pe-0">
                      {finalized ? '—' : (status?.NextDueAt === 'now' ? 'Now' : fmt(status?.NextDueAt))}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted ps-0">Periodic sync</td>
                    <td className="text-end pe-0">
                      {status?.SyncEnabled
                        ? <Badge color="success">every {status.IntervalMinutes} min</Badge>
                        : <Badge color="secondary">off</Badge>}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {canFinalize && !finalized &&
                <Button color="outline-dark" className="w-100 mt-3" onClick={() => setFinalizeModal(true)}>
                  Finalize — hand over to LapStart
                </Button>
              }
            </CardBody>
          </Card>
        </Col>

        <Col md={8}>
          <Card>
            <CardBody>
              <CardTitle className="mb-3">Connection</CardTitle>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Base URL</Label>
                    <Input
                      value={settings.BaseUrl}
                      placeholder="https://lapstart.example.com"
                      disabled={!canUpdate}
                      onChange={(e) => setSettings({ ...settings, BaseUrl: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Roster Token</Label>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={settings.RosterToken}
                      placeholder={status?.HasToken ? '•••••••• (leave blank to keep)' : 'Paste the token from LapStart'}
                      disabled={!canUpdate}
                      onChange={(e) => setSettings({ ...settings, RosterToken: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Sync every (minutes)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={1440}
                      value={settings.IntervalMinutes}
                      disabled={!canUpdate}
                      onChange={(e) => setSettings({ ...settings, IntervalMinutes: parseInt(e.target.value, 10) || 1 })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mt-md-4 pt-md-2">
                    <div className="form-check form-switch form-switch-md">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="lapstartSyncEnabled"
                        checked={settings.SyncEnabled}
                        disabled={!canUpdate}
                        onChange={(e) => setSettings({ ...settings, SyncEnabled: e.target.checked })}
                      />
                      <Label className="form-check-label" for="lapstartSyncEnabled">Enable periodic sync</Label>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              {canUpdate &&
                <Button color="primary" onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? 'Saving…' : 'Save Settings'}
                </Button>
              }
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/*
        Queued, not stuck. Absence from a push means nothing to LapStart, so each
        of these needs an explicit delete call -- which the next sync makes.
      */}
      {pendingDeletions.length > 0 &&
        <Card>
          <CardBody>
            <CardTitle className="mb-1">
              Queued for removal from LapStart <Badge color="info">{pendingDeletions.length}</Badge>
            </CardTitle>
            <p className="text-muted small">
              Deleted here after being sent to LapStart. The next sync removes each one by name, along with its
              chip binding. Until then they are still on LapStart's start list.
            </p>
            <DeletionTable rows={pendingDeletions} />
          </CardBody>
        </Card>
      }

      {/*
        "You may delete what you gave us; you may not delete what we made."
        Permanent until a human intervenes, so these are kept out of the retry
        set entirely rather than failing on every run.
      */}
      {blockedDeletions.length > 0 &&
        <Card>
          <CardBody>
            <CardTitle className="mb-1">
              LapStart refused to remove these <Badge color="danger">{blockedDeletions.length}</Badge>
            </CardTitle>
            <p className="text-muted small">
              These have timing data recorded against them, so LapStart will not delete them and expo has stopped
              retrying. A withdrawn runner who nonetheless has timing reads is worth a look either way —
              <strong> take it to a LapStart operator</strong>, who can remove them in the ops console.
            </p>
            <DeletionTable rows={blockedDeletions} showReason />
          </CardBody>
        </Card>
      }

      <Card>
        <CardBody>
          <CardTitle className="mb-3">Sync History</CardTitle>
          <div className="table-responsive">
            <Table size="sm" hover className="mb-0">
              <thead>
                <tr>
                  <th>When</th><th>Trigger</th><th>Status</th>
                  <th className="text-end">Created</th>
                  <th className="text-end">Updated</th>
                  <th className="text-end">Unchanged</th>
                  <th className="text-end">Deleted</th>
                  <th className="text-end">Rejected</th>
                  <th className="text-end">Skipped</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 &&
                  <tr><td colSpan={10} className="text-center text-muted py-4">No syncs yet.</td></tr>
                }
                {logs.map(log => (
                  <tr key={log.id}>
                    <td>{fmt(log.started_at)}</td>
                    <td><span className="text-muted">{log.trigger}</span>{log.triggered_by ? ' · ' + log.triggered_by.name : ''}</td>
                    <td><Badge color={statusColor[log.status] || 'secondary'}>{log.status}</Badge></td>
                    <td className="text-end">{log.created}</td>
                    <td className="text-end">{log.updated}</td>
                    <td className="text-end">{log.unchanged}</td>
                    <td className="text-end">{log.deleted > 0 ? <span className="text-primary">{log.deleted}</span> : 0}</td>
                    <td className="text-end">{log.rejected > 0 ? <span className="text-danger">{log.rejected}</span> : 0}</td>
                    <td className="text-end">{log.skipped > 0 ? <span className="text-warning">{log.skipped}</span> : 0}</td>
                    <td className="text-end">
                      {(log.rejections?.length > 0 || log.message) &&
                        <Button size="sm" color="link" className="p-0" onClick={() => setResultModal({ visible: true, log })}>
                          Details
                        </Button>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <ResultModal
        config={resultModal}
        onClose={() => setResultModal({ visible: false, log: null })}
      />

      <Modal isOpen={finalizeModal} toggle={() => setFinalizeModal(false)}>
        <ModalHeader toggle={() => setFinalizeModal(false)}>Finalize the roster?</ModalHeader>
        <ModalBody>
          <p>
            After this, expo stops sending to LapStart entirely — no creates, no updates, no deletions.
            LapStart owns the roster and every further change goes through its ops console.
          </p>
          {/*
            Finalizing over unresolved items is sometimes the right call at 05:50.
            It should be possible, but never accidental.
          */}
          {(status?.LastRun?.rejected > 0 || pendingDeletions.length > 0 || blockedDeletions.length > 0) &&
            <Alert color="warning" className="mb-0">
              <strong>Still outstanding:</strong>
              <ul className="mb-0 mt-1">
                {status?.LastRun?.rejected > 0 && <li>{status.LastRun.rejected} rejected row(s) on the last sync</li>}
                {pendingDeletions.length > 0 &&
                  <li>{pendingDeletions.length} queued deletion(s) — finalizing now leaves them on LapStart's start list</li>}
                {blockedDeletions.length > 0 &&
                  <li>{blockedDeletions.length} deletion(s) LapStart refused, needing an operator</li>}
              </ul>
            </Alert>
          }
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setFinalizeModal(false)}>Cancel</Button>
          <Button color="dark" onClick={doFinalize}>Finalize</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

const DeletionTable = ({ rows, showReason }) => (
  <div className="table-responsive">
    <Table size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Bib</th><th>Name</th><th>Race</th><th>Deleted here</th>
          {showReason && <th>Reason</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id}>
            <td>{row.bib_number}</td>
            <td>{row.name}</td>
            <td>{row.race || '—'}</td>
            <td>{row.deleted_at ? moment(row.deleted_at).format('MMM Do, YYYY HH:mm:ss') : '—'}</td>
            {showReason && <td><code>{row.blocked_reason}</code></td>}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
)

/**
 * The push response is the only view of LapStart's state we ever get, so a
 * rejection has to be readable rather than counted. `message` is written for an
 * operator standing at a desk -- show it verbatim, and branch on `reason`.
 */
const ResultModal = ({ config, onClose }) => {
  const log = config.log
  if (!log) return null

  const rejections = log.rejections || []
  const ignored = log.ignored_fields || []

  return (
    <Modal isOpen={config.visible} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>Sync Result</ModalHeader>
      <ModalBody>
        <Row className="text-center mb-3">
          {[
            ['Created', log.created, 'success'],
            ['Updated', log.updated, 'info'],
            ['Unchanged', log.unchanged, 'secondary'],
            // deleted and not-found are both "gone from LapStart", but they are
            // not the same event and folding them together would claim we
            // removed people we did not.
            ['Deleted', log.deleted, 'primary'],
            ['Already gone', log.delete_not_found, 'secondary'],
            ['Rejected', log.rejected, 'danger'],
            ['Skipped', log.skipped, 'warning'],
          ].map(([label, value, color]) => (
            <Col key={label}>
              <div className={'h4 mb-0 text-' + (value > 0 ? color : 'muted')}>{value}</div>
              <small className="text-muted">{label}</small>
            </Col>
          ))}
        </Row>

        {log.message && <Alert color={log.status === 'failed' ? 'danger' : 'warning'}>{log.message}</Alert>}

        {/*
          An empty ignoredFields is the healthy state. Anything in it means expo
          sent a key LapStart has never heard of -- almost always a typo on our
          side, and the alarm exists precisely so it surfaces on the first push.
        */}
        {ignored.length > 0 &&
          <Alert color="warning">
            <strong>LapStart ignored these fields:</strong> {ignored.join(', ')}.
            <div className="small">This is an expo-side bug — those values were not stored.</div>
          </Alert>
        }

        {rejections.length > 0 &&
          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr><th>Bib</th><th>Name</th><th>Reason</th><th>What happened</th></tr>
              </thead>
              <tbody>
                {rejections.map((r, i) => (
                  <tr key={i}>
                    <td className="text-nowrap">{r.bib || '—'}</td>
                    <td>{r.raceKitName || '—'}</td>
                    <td className="text-nowrap"><code>{r.reason}</code></td>
                    <td>
                      {r.message}
                      {r.conflict?.runnerName &&
                        <div className="small text-muted">In the way: {r.conflict.runnerName} ({r.conflict.raceSlug})</div>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        }

        {rejections.length === 0 && !log.message &&
          <p className="text-muted mb-0">Every participant landed.</p>
        }
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}
